import { t } from 'i18next';
import { z } from 'zod';

import { zu } from '@/lib/zod/zod-utils';
import { type InterviewType } from '@/services/api';

import type { InterviewQuestionFormFields } from '../admin-new-interview/common';
import { QUESTION_WEIGHTS, THINKING_TIMES } from './constants';

export const zFormFieldsCandidateAdd = (isInterviewAssistant: boolean) =>
  z
    .object({
      fullName: zu.string.nonEmpty(z.string(), {
        required_error: t('auth:users.data.fullName.required'),
      }),
      email: zu.string.email(z.string(), {
        required_error: t('auth:users.data.email.required'),
        invalid_type_error: t('auth:users.data.email.invalid'),
      }),
      sendInvitationMail: z.boolean(),
      phoneNumber: z
        .string()
        .regex(/^\+?[\d\s-]{10,15}$/, {
          message: t('auth:users.data.phoneNumber.invalid'),
        })
        .optional()
        .or(z.literal('')),
      turkishIdNumber: z
        .string()
        .regex(/^\d{11}$/, {
          message: t('common:information.turkishIdNumberInvalidError'),
        })
        .optional()
        .or(z.literal('')),
      link: z.string().optional(),
      startDate: z.date().optional(),
      startTime: z.string().optional(),
    })
    .superRefine((data, ctx) => {
      if (isInterviewAssistant) {
        if (!data.link || data.link.trim() === '') {
          ctx.addIssue({
            path: ['link'],
            code: 'custom',
            message: t('auth:users.data.link.required'),
          });
        }
        if (!data.startDate) {
          ctx.addIssue({
            path: ['startDate'],
            code: 'custom',
            message: t('auth:users.data.meetingDate.required'),
          });
        }
        if (!data.startTime || data.startTime.trim() === '') {
          ctx.addIssue({
            path: ['startTime'],
            code: 'custom',
            message: t('auth:users.data.meetingTime.required'),
          });
        }
      }
    });

export type FormFieldsCandidateAdd = z.infer<
  ReturnType<typeof zFormFieldsCandidateAdd>
>;

export const zFormFieldsAssistantCandidateAdd = () =>
  z.object({
    fullName: zu.string.nonEmpty(z.string(), {
      required_error: t('auth:users.data.fullName.required'),
    }),
    email: zu.string.email(z.string(), {
      required_error: t('auth:users.data.email.required'),
      invalid_type_error: t('auth:users.data.email.invalid'),
    }),
    phoneNumber: z
      .string()
      .regex(/^\+?[\d\s-]{10,15}$/, {
        message: t('auth:users.data.phoneNumber.invalid'),
      })
      .optional()
      .or(z.literal('')),
    link: zu.string.nonEmpty(z.string(), {
      required_error: t('auth:users.data.link.required'),
    }),
    meetingDate: z.date({
      required_error: t('auth:users.data.meetingDate.required'),
    }),
    meetingTime: zu.string.nonEmpty(z.string(), {
      required_error: t('auth:users.data.meetingTime.required'),
    }),
  });
export type FormFieldsAssistantCandidateAdd = z.infer<
  ReturnType<typeof zFormFieldsAssistantCandidateAdd>
>;

export const zFormFieldsShareCandidates = () =>
  z.object({
    nameSurname: zu.string.nonEmpty(z.string()),
    email: zu.string.email(z.string()),
    description: z.string(),

    selectedCandidates: z.array(z.string()).nonempty({
      message: t('candidate:share.selectedCandidatesValidationMsg'),
    }),
    isHideUserInfo: z.boolean().optional(),
  });
export type FormFieldsShareCandidates = z.infer<
  ReturnType<typeof zFormFieldsShareCandidates>
>;

export const zFormFieldsInterviewQuestionSettings = () =>
  z.object({
    thinkingTime: z.enum(THINKING_TIMES).nullable(),
    questionWeight: z.enum(QUESTION_WEIGHTS).nullable().optional(),
    skillTitle: z.string().optional(),
    // skillTitle: zu.string.nonEmpty(z.string(), {
    //   required_error: t(
    //     'interview:createNewInterview.questionPreparation.skillTitleRequired'
    //   ),
    // }),
    ignoreRating: z.boolean().optional(),
    answerAgain: z.boolean().optional(),
    isAdminQuestion: z.boolean().optional(),
    tags: z.array(z.string()).optional(),
  });
export type FormFieldsInterviewQuestionSettings = z.infer<
  ReturnType<typeof zFormFieldsInterviewQuestionSettings>
>;

export type EditInterviewQuestionsFormInputs = {
  type: InterviewType;
  questions: Array<
    InterviewQuestionFormFields & {
      id?: string;
      prevImages?: Array<string>;
      prevMediaFiles?: Array<string>;
    }
  >;
};
